import { type CSSProperties, type ReactNode } from 'react'
import { COLORS } from '../lib/theme'

// Shared building blocks for the WeHunt auth screens (sign-in / sign-up) so the
// two pages stay visually identical. All token-themed (dark mode) + responsive.

export const authInputStyle: CSSProperties = {
  width: '100%',
  height: 52,
  padding: '0 14px',
  fontSize: 15,
  border: `1px solid ${COLORS.outlineVariant}`,
  borderRadius: 12,
  outline: 'none',
  background: COLORS.surfaceContainerLowest,
  color: COLORS.onSurface,
  boxSizing: 'border-box',
}

/** Centered, responsive card on the themed page background. */
export function AuthShell({ children, banner }: { children: ReactNode; banner?: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: COLORS.surface,
        boxSizing: 'border-box',
      }}
    >
      {banner}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          boxSizing: 'border-box',
        }}
      >
        <div
          className="wh-card"
          style={{
            width: '100%',
            maxWidth: 460,
            padding: 'clamp(24px, 5vw, 44px)',
            background: COLORS.surfaceContainerLowest,
            borderRadius: 18,
            boxShadow: '0 18px 50px rgba(0,0,0,0.12)',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

/**
 * A brand mark and its alt text travel together: this component is shared by
 * WeHunt and Viltrapport, so defaulting the alt to either product's name would
 * silently mislabel the other one's logo. Requiring both together makes that a
 * compile error at the call site instead.
 */
type AuthHeaderLogo =
  | { logoSrc: string; logoAlt: string }
  | { logoSrc?: undefined; logoAlt?: undefined }

export function AuthHeader({
  eyebrow,
  logoSrc,
  logoAlt,
  title,
  subtitle,
  align = 'start',
}: AuthHeaderLogo & {
  eyebrow?: string
  title: string
  subtitle: string
  /**
   * Horizontal alignment of the whole header. `start` keeps the original
   * left-aligned eyebrow layout; `center` centres the mark, title and subtitle
   * together. Kept a separate knob from `logoSrc` so a logo does not silently
   * change alignment.
   */
  align?: 'start' | 'center'
}) {
  const centered = align === 'center'
  return (
    <>
      {logoSrc && (
        <img
          src={logoSrc}
          alt={logoAlt}
          width={52}
          height={52}
          // The app-icon art is a full-bleed square with an opaque gradient, so
          // the radius clips the image itself; there is no pre-rounded variant.
          style={{ display: 'block', margin: centered ? '0 auto 6px' : '0 0 6px', width: 52, height: 52, borderRadius: 12 }}
        />
      )}
      {!logoSrc && eyebrow && (
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.primary, textAlign: align }}>
          {eyebrow}
        </div>
      )}
      <h1 style={{ margin: '10px 0 8px', fontFamily: COLORS.fontDisplay, fontSize: 'clamp(34px, 6vw, 46px)', fontWeight: 500, lineHeight: 1.05, color: COLORS.onSurface, textAlign: align }}>
        {title}
      </h1>
      <p style={{ margin: '0 0 26px', fontSize: 15, color: COLORS.onSurfaceVariant, lineHeight: 1.5, textAlign: align }}>
        {subtitle}
      </p>
    </>
  )
}

export function SsoButton({ children, icon, onClick, disabled }: { children: ReactNode; icon: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        height: 52, width: '100%', borderRadius: 12,
        border: `1px solid ${COLORS.outlineVariant}`,
        background: COLORS.surfaceContainerLowest,
        color: COLORS.onSurface, fontSize: 15, fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      }}
    >
      {icon}
      {children}
    </button>
  )
}

export function Divider({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
      <span style={{ flex: 1, height: 1, background: COLORS.outlineVariant }} />
      <span style={{ fontSize: 13, color: COLORS.onSurfaceVariant, whiteSpace: 'nowrap' }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: COLORS.outlineVariant }} />
    </div>
  )
}

export function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  )
}

export function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}
